/**
 * supabaseDocumentValidation.ts — pure Validation- und Pfad-Helper für
 * den Multi-Dokument-Upload (case_documents).
 *
 * BEWUSST frei von React und Supabase-Imports, damit die Tests in Vitest
 * laufen, ohne den gesamten Storage-Client zu mocken (gleiches Pattern wie
 * bwaUploadValidation.ts).
 *
 * Storage-Pfad-Konvention (siehe Migration 0016):
 *   cases/{case_id}/{doc_type}/{ts}-{sanitized_filename}
 *
 * Die RLS-Policy extrahiert die case_id aus dem ZWEITEN Pfad-Segment via
 * `storage.foldername(name)[2]::uuid`. Wenn der Pfad nicht mit "cases/"
 * beginnt, schlägt die RLS bereits fehl.
 */

import type { DocumentType } from './supabaseTypes';

// Re-export für Konsumenten, die die Validation-Schicht direkt nutzen oder
// (häufiger) den Typ via supabaseDocuments.ts erwarten.
export type { DocumentType };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Maximale Dateigröße — synchron zu Migration 0016 (size_bytes-CHECK). */
export const CASE_DOCUMENT_MAX_BYTES = 20 * 1024 * 1024; // 20 MiB

/**
 * Akzeptierte MIME-Types — synchron zu allowed_mime_types des Buckets in 0016.
 * Bilder sind dabei (Smartphone-Foto vom Mietvertrag), DATEV-Excel auch.
 */
export const CASE_DOCUMENT_ACCEPTED_MIMES: ReadonlyArray<string> = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/** Dateiendungen-Whitelist für den Browser-Fallback bei octet-stream-MIME. */
export const CASE_DOCUMENT_ACCEPTED_EXTENSIONS: ReadonlyArray<string> = [
  '.pdf',
  '.xlsx',
  '.xls',
  '.csv',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.doc',
  '.docx',
];

/** Single-Slot-Typen: pro Case nur EIN aktives Dokument je Typ. */
export const SINGLE_SLOT_DOCUMENT_TYPES: ReadonlySet<DocumentType> = new Set<DocumentType>([
  'bwa',
  'eks',
  'mietvertrag',
  'steuernummer_nachweis',
]);

const ALL_DOCUMENT_TYPES: ReadonlySet<DocumentType> = new Set<DocumentType>([
  'bwa',
  'eks',
  'mietvertrag',
  'steuernummer_nachweis',
  'sonstiges',
]);

export type DocumentValidationError = 'too-large' | 'wrong-type' | 'empty';

export interface DocumentValidationResult {
  readonly ok: boolean;
  readonly error?: DocumentValidationError;
}

/**
 * Type-Guard: ist der gegebene String ein gültiger DocumentType?
 * Nützlich für Routing-Param-Validation oder URL-Query-Parsing.
 */
export function isValidDocumentType(v: string): v is DocumentType {
  return ALL_DOCUMENT_TYPES.has(v as DocumentType);
}

/**
 * Validiert eine Datei vor dem Upload. Pure Function — kein Side-Effect.
 *
 * Reihenfolge: empty (defensive) → too-large → wrong-type. Die Größenprüfung
 * vor der Type-Prüfung schützt vor unnötigem Type-Sniffing bei großen Files.
 */
export function validateUploadFile(file: {
  size: number;
  type: string;
  name: string;
}): DocumentValidationResult {
  if (file.size === 0) return { ok: false, error: 'empty' };
  if (file.size > CASE_DOCUMENT_MAX_BYTES) return { ok: false, error: 'too-large' };

  // Pfad 1: MIME-Type matcht direkt
  if (CASE_DOCUMENT_ACCEPTED_MIMES.includes(file.type)) return { ok: true };

  // Pfad 2: Endung-Fallback (Browser-MIME-Sniffing variiert, vor allem bei
  // Smartphone-Fotos und DATEV-Excel-Exports)
  const lower = file.name.toLowerCase();
  if (CASE_DOCUMENT_ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return { ok: true };
  }

  return { ok: false, error: 'wrong-type' };
}

/**
 * Reduziert einen Dateinamen auf einen sicheren Slug. Ungültige Zeichen
 * werden zu `_`, mehrere `_` zu einem zusammengezogen, Leading/Trailing
 * Punkte/Underscores entfernt. Endung bleibt erhalten.
 *
 * Längen-Cap: 100 Zeichen für base + Endung — schützt gegen
 * Storage-Pfad-Limits (Supabase Storage tendenziell unproblematisch, aber
 * Nginx/Edge-Caches haben URL-Längen-Limits).
 *
 * Umlaute werden via Unicode-Normalisierung (NFKD) zerlegt und die
 * Diakritika anschließend gestrippt — `ä` → `a`, `ö` → `o`, `ü` → `u`.
 */
export function sanitizeFilename(name: string): string {
  // Letzten Punkt finden, um Endung zu bewahren. Wenn die "Endung" nur aus
  // weiteren Punkten besteht (z.B. Eingabe "...."), gilt sie als leer.
  const dotIdx = name.lastIndexOf('.');
  const base = dotIdx > 0 ? name.slice(0, dotIdx) : name;
  let ext = dotIdx > 0 ? name.slice(dotIdx).toLowerCase() : '';
  // Eine Endung nur aus '.' (oder noch mehr Punkten) ist keine Endung.
  // Ebenfalls Endungen mit nicht-alphanumerischem Inhalt verwerfen wir
  // (Pfad-Sicherheit; ungewöhnliche Endungen sind auch nicht in der
  // Whitelist erlaubt).
  if (!/^\.[a-z0-9]+$/i.test(ext)) {
    ext = '';
  }

  const cleanBase = base
    // 1. NFKD: zerlegt 'ä' in 'a' + Combining-Diaeresis (U+0308)
    .normalize('NFKD')
    // 2. Combining-Diakritika (U+0300..U+036F) strippen (ohne sie würden
    //    Umlaute später durch das Regex-Replace zu '_a' statt 'a' werden)
    .replace(/[̀-ͯ]/g, '')
    // 3. Alles außer ASCII-Buchstaben/Zahlen/._- → _
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    // 4. Mehrere _ zu einem zusammenziehen
    .replace(/_+/g, '_')
    // 5. Leading/Trailing Müll entfernen
    .replace(/^[._]+|[._]+$/g, '');

  const maxBaseLen = Math.max(1, 100 - ext.length);
  const truncated = cleanBase.slice(0, maxBaseLen);

  if (truncated.length === 0) {
    return `dokument${ext}`;
  }
  return `${truncated}${ext}`;
}

/**
 * Baut den Storage-Pfad für ein Case-Dokument.
 *
 * Pfad-Format MUSS `cases/{caseId}/{docType}/{ts}-{filename}` sein
 * (siehe Migration 0016, Storage-RLS extrahiert case_id aus
 * storage.foldername()[2]).
 *
 * - caseId muss UUID sein (sonst greift RLS nicht)
 * - docType muss aus der Whitelist sein
 * - filename wird desinfiziert (sanitizeFilename)
 * - Timestamp-Prefix (ISO, : und . zu - ersetzt) für Multi-Upload-Pro-Slot
 *   und um Kollisionen über die Versionshistorie zu vermeiden
 */
export function buildCaseDocumentPath(
  caseId: string,
  docType: DocumentType,
  originalName: string,
  now: () => Date = () => new Date(),
): string {
  if (!UUID_RE.test(caseId)) {
    throw new Error('buildCaseDocumentPath: caseId muss UUID sein');
  }
  if (!isValidDocumentType(docType)) {
    // docType wird nach dem Type-Guard zu `never` verengt → Templatierung
    // nur via String-Cast, sonst @typescript-eslint/restrict-template-expressions.
    throw new Error(`buildCaseDocumentPath: ungültiger docType "${String(docType)}"`);
  }

  const sanitized = sanitizeFilename(originalName);
  const ts = now().toISOString().replace(/[:.]/g, '-');
  return `cases/${caseId}/${docType}/${ts}-${sanitized}`;
}
